const launchSelector = '[data-editorial-product-launch]';
const overlaySelector = '.ed-product-overlay-shell';
const closeSelector = '[data-editorial-product-overlay-close]';
const productExitSelector = '.ed-product-overlay-shell .product-exit__text';
const detailsSelector = '.ed-product-overlay-shell__details';
const mediaSelector = '.ed-product-overlay-shell__media';
const shopMoreSelector = '.ed-product-overlay-shell__shop-more';
const overlayEnteredClass = 'ed-product-overlay-shell--entered';
const launchStorageKey = 'wndrfm:editorial-product-launch';
const mobileReturnKey = 'wndrfm:editorial-mobile-return';
const overlayCloseAnimationName = 'ed-product-overlay-close';
const overlayMobileQuery = window.matchMedia('(max-width: 999px)');

function shouldUseEditorialOverlay() {
  return true;
}

function getLaunchContext() {
  try {
    const context = JSON.parse(sessionStorage.getItem(launchStorageKey) || 'null');
    if (!context?.from || !context?.to) return null;

    const from = new URL(context.from, window.location.href);
    const to = new URL(context.to, window.location.href);
    if (from.origin !== window.location.origin || to.origin !== window.location.origin) return null;

    return {
      from: from.href,
      to: to.href,
      reopen: Boolean(context.reopen),
      openOnHome: Boolean(context.openOnHome),
    };
  } catch (_error) {
    return null;
  }
}

function setLaunchContext(context) {
  try {
    sessionStorage.setItem(launchStorageKey, JSON.stringify(context));
  } catch (_error) {
    // Navigation should continue even when storage is unavailable.
  }
}

function clearLaunchContext() {
  try {
    sessionStorage.removeItem(launchStorageKey);
  } catch (_error) {
    // Storage is optional.
  }
}

function getOverlayShell() {
  return document.querySelector(overlaySelector);
}

function getHomeUrl() {
  const revealLink = document.querySelector('.ed-product-reveal-link');
  return new URL(revealLink?.getAttribute('href') || '/', window.location.href).href;
}

function clearLaunchHiding() {
  document.documentElement.classList.remove('editorial-product-redirecting', 'editorial-product-hydrating');
}

function markMobileEditorialReturn() {
  try {
    sessionStorage.setItem(mobileReturnKey, '1');
  } catch (_error) {
    // Storage is optional.
  }
}

function clearMobileEditorialReturn() {
  try {
    sessionStorage.removeItem(mobileReturnKey);
  } catch (_error) {
    // Storage is optional.
  }
}

function handleMobileHomepageReturn() {
  if (!document.body.classList.contains('template-index')) return;

  let shouldDisableSmoothScroll = false;

  try {
    shouldDisableSmoothScroll = sessionStorage.getItem(mobileReturnKey) === '1';
  } catch (_error) {
    shouldDisableSmoothScroll = false;
  }

  if (!shouldDisableSmoothScroll) return;

  document.documentElement.classList.add('editorial-scroll-restore');

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('editorial-scroll-restore');
      clearMobileEditorialReturn();
    });
  });
}

function closeOverlayShell({ restoreUrl = false, animate = false } = {}) {
  const shell = getOverlayShell();
  if (!shell) return;

  const finishClose = () => {
    shell.removeEventListener('animationend', handleCloseAnimationEnd);
    shell.hidden = true;
    shell.classList.remove(overlayEnteredClass);
    shell.removeAttribute('data-state');
    delete shell.dataset.productUrl;
    clearOverlayContent(shell);
    document.body.classList.remove('ed-product-overlay-open', 'ed-product-overlay-closing');
    document.documentElement.classList.remove('editorial-product-launching');
    clearLaunchHiding();

    if (restoreUrl) {
      const context = getLaunchContext();
      history.replaceState({}, '', context?.from || '/');
    }

    clearLaunchContext();
  };

  const handleCloseAnimationEnd = (event) => {
    if (event.target !== shell || event.animationName !== overlayCloseAnimationName) return;
    finishClose();
  };

  if (animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    shell.removeEventListener('animationend', handleCloseAnimationEnd);
    shell.addEventListener('animationend', handleCloseAnimationEnd);
    document.body.classList.add('ed-product-overlay-closing');
    return;
  }

  finishClose();
}

function closeOverlayFromControl() {
  closeOverlayShell({ restoreUrl: true, animate: true });
}

function setOverlayState(shell, state) {
  shell.dataset.state = state;
}

function clearOverlayContent(shell) {
  const details = shell.querySelector(detailsSelector);
  const media = shell.querySelector(mediaSelector);
  const shopMore = shell.querySelector(shopMoreSelector);

  if (details) details.replaceChildren();
  if (media) media.replaceChildren();
  if (shopMore) shopMore.replaceChildren();
}

function loadExternalScript(script) {
  if (!script.src) return Promise.resolve();

  const existingScript = document.querySelector(`script[src="${script.src}"]`);
  if (existingScript) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const nextScript = document.createElement('script');
    nextScript.src = script.src;
    nextScript.type = script.type || 'text/javascript';
    nextScript.async = false;
    nextScript.onload = () => resolve();
    nextScript.onerror = () => reject(new Error(`Failed to load script: ${script.src}`));
    document.head.append(nextScript);
  });
}

function loadProductScripts(doc) {
  const scripts = Array.from(doc.querySelectorAll('script[src][type="module"]'));
  const productScripts = scripts.filter((script) =>
    ['zoom-dialog.js', 'drag-zoom-wrapper.js', 'sticky-add-to-cart.js'].some((assetName) => script.src.includes(assetName))
  );

  return Promise.all(productScripts.map(loadExternalScript));
}

function splitDetailsContent(detailsWrapper) {
  const wrapperClone = detailsWrapper.cloneNode(true);
  const exitSection = wrapperClone.querySelector('.exit-section');

  if (exitSection) {
    exitSection.remove();
  }

  return {
    exitSection,
    detailsContent: wrapperClone,
  };
}

function isMobileOverlay() {
  return overlayMobileQuery.matches;
}

function buildMobileMediaStack(productMedia) {
  const sourceImages = Array.from(productMedia.querySelectorAll('.product-media > .product-media__image'));

  if (sourceImages.length === 0) {
    return productMedia.cloneNode(true);
  }

  const stack = document.createElement('div');
  stack.className = 'ed-product-overlay-shell__media-carousel';

  for (const sourceImage of sourceImages) {
    const stackItem = document.createElement('div');
    stackItem.className = 'ed-product-overlay-shell__media-carousel-item';

    const imageClone = sourceImage.cloneNode(true);
    imageClone.classList.add('ed-product-overlay-shell__media-carousel-image');

    stackItem.appendChild(imageClone);
    stack.appendChild(stackItem);
  }

  return stack;
}

async function loadProductContent(shell, url) {
  setOverlayState(shell, 'loading');
  clearOverlayContent(shell);

  const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
  if (!response.ok) throw new Error(`Failed to load product page: ${response.status}`);

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const productDetails = doc.querySelector('.details-wrapper');
  const productMedia = doc.querySelector('[data-testid="product-information-media"]');
  const shopMore = doc.querySelector('.editorial-shop-more');
  const detailsTarget = shell.querySelector(detailsSelector);
  const mediaTarget = shell.querySelector(mediaSelector);
  const shopMoreTarget = shell.querySelector(shopMoreSelector);

  if (!productDetails || !productMedia || !detailsTarget || !mediaTarget || !shopMoreTarget) {
    throw new Error('Product overlay content was not found.');
  }

  await loadProductScripts(doc);

  const { exitSection, detailsContent } = splitDetailsContent(productDetails);
  const isMobile = isMobileOverlay();

  const detailsFragment = document.createDocumentFragment();

  if (exitSection) {
    const exitWrapper = document.createElement('div');
    exitWrapper.className = 'ed-product-overlay-shell__details-exit';
    exitWrapper.appendChild(exitSection);
    detailsFragment.appendChild(exitWrapper);
  }

  const detailsWrapper = document.createElement('div');
  detailsWrapper.className = 'ed-product-overlay-shell__details-content';
  detailsWrapper.appendChild(detailsContent);

  if (isMobile) {
    const inlineMediaWrapper = document.createElement('div');
    inlineMediaWrapper.className = 'ed-product-overlay-shell__details-media';
    inlineMediaWrapper.appendChild(buildMobileMediaStack(productMedia));
    detailsFragment.appendChild(inlineMediaWrapper);
  }

  detailsFragment.appendChild(detailsWrapper);

  detailsTarget.replaceChildren(detailsFragment);
  mediaTarget.replaceChildren(...(isMobile ? [] : [productMedia.cloneNode(true)]));
  shopMoreTarget.replaceChildren(...(shopMore ? [shopMore.cloneNode(true)] : []));
  setOverlayState(shell, 'loaded');
}

function openOverlayShell(url, from = window.location.href) {
  const shell = getOverlayShell();
  if (!shell) return false;

  shell.classList.remove(overlayEnteredClass);
  shell.hidden = false;
  shell.dataset.productUrl = url;
  document.body.classList.add('ed-product-overlay-open');
  document.documentElement.classList.add('editorial-product-launching');
  clearLaunchHiding();

  shell.addEventListener(
    'animationend',
    (event) => {
      if (event.animationName === 'ed-product-overlay-slide-in') {
        shell.classList.add(overlayEnteredClass);
      }
    },
    { once: true }
  );

  history.pushState({ editorialProductOverlay: true, from }, '', url);

  loadProductContent(shell, url).catch((error) => {
    console.error(error);
    setOverlayState(shell, 'error');
  });

  return true;
}

function reloadOpenOverlayForLayoutChange() {
  const shell = getOverlayShell();
  if (!shell || shell.hidden || !shell.dataset.productUrl) return;

  loadProductContent(shell, shell.dataset.productUrl).catch((error) => {
    console.error(error);
    setOverlayState(shell, 'error');
  });
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const closeButton = event.target instanceof Element ? event.target.closest(closeSelector) : null;
  if (closeButton) {
    event.preventDefault();

    closeOverlayFromControl();
    return;
  }

  const productExit = event.target instanceof Element ? event.target.closest(productExitSelector) : null;
  if (productExit) {
    event.preventDefault();
    closeOverlayFromControl();
    return;
  }

  const link = event.target instanceof Element ? event.target.closest(launchSelector) : null;
  if (!(link instanceof HTMLAnchorElement)) return;

  if (!shouldUseEditorialOverlay()) {
    markMobileEditorialReturn();
    return;
  }

  const editorialUrl = link.dataset.editorialProductUrl;
  if (!editorialUrl) return;

  event.preventDefault();

  const resolvedEditorialUrl = new URL(editorialUrl, window.location.href).href;

  setLaunchContext({
    from: window.location.href,
    to: resolvedEditorialUrl,
  });

  if (openOverlayShell(resolvedEditorialUrl, window.location.href)) return;

  document.documentElement.classList.add('editorial-product-launching');
  window.location.href = resolvedEditorialUrl;
});

window.addEventListener('popstate', () => {
  if (!history.state?.editorialProductOverlay) {
    closeOverlayShell({ animate: true });
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !getOverlayShell() || getOverlayShell()?.hidden) return;

  if (history.state?.editorialProductOverlay) {
    closeOverlayFromControl();
  } else {
    closeOverlayShell({ restoreUrl: true, animate: true });
  }
});

overlayMobileQuery.addEventListener('change', reloadOpenOverlayForLayoutChange);

if (document.body.classList.contains('template-product-editorial') && shouldUseEditorialOverlay()) {
  setLaunchContext({
    from: getHomeUrl(),
    to: window.location.href,
    openOnHome: true,
  });
}

const launchContext = getLaunchContext();

if (launchContext?.openOnHome && document.body.classList.contains('template-index') && shouldUseEditorialOverlay()) {
  setLaunchContext({ ...launchContext, openOnHome: false });
  if (!openOverlayShell(launchContext.to, launchContext.from)) clearLaunchHiding();
} else {
  clearLaunchHiding();
}

handleMobileHomepageReturn();
window.addEventListener('pageshow', handleMobileHomepageReturn);
